<template>
  <LcCard
    :aria-disabled="!enabled"
    :aria-label="ariaEnabledLabel"
    body-class="p-none"
    class="prize-banner mx-100"
    role="button"
    shadow="large"
    :tabindex="enabled ? 0 : -1"
    @click="openModal"
    @keydown.enter.prevent="openModal"
    @keydown.space.prevent="openModal"
  >
    <HighFrequencyGamePrizeBannerInner
      class="px-200 py-100 rounded-m"
      :class="{ 'inner-enabled': enabled }"
      variant="banner"
    >
      <Transition name="fade">
        <LcIcon v-if="enabled" class="open-icon" name="chevron-right" size="small" />
      </Transition>
    </HighFrequencyGamePrizeBannerInner>
  </LcCard>
</template>

<script lang="ts" setup>
type Emits = {
  (e: 'click'): void
}

defineOptions({
  inheritAttrs: false,
})

const emit = defineEmits<Emits>()

const { t } = useI18n()
const { prizeTier, visibleJackpot } = useHighFrequencyGame()

const ariaEnabledLabel = computed(() =>
  enabled.value
    ? t('highfrequencygame.prizebanner.arialabel.enabled', {
        prize: formatMoney(visibleJackpot.value),
      })
    : t('highfrequencygame.prizebanner.arialabel.disabled', {
        prize: formatMoney(visibleJackpot.value),
      }),
)

const enabled = computed(() => !!prizeTier.value)

const openModal = () => {
  if (!enabled.value) return
  emit('click')
}
</script>

<style lang="scss" scoped>
.prize-banner {
  .inner-enabled {
    cursor: pointer;
  }

  .open-icon {
    position: absolute;
    top: 50%;
    right: token('size/space/gap/100');
    width: 0.75rem;
    height: 0.75rem;
    border-radius: token('size/radius/s');
    background-color: token('color/background/accent/win/win-bold');
    color: token('color/icon/secondary/secondary');
    font-size: 0.75rem;
    transform: translateY(-50%);
  }

  @include focus-state {
    &::before {
      inset: token('size/border/200');
      z-index: 1;
      border-radius: token('size/radius/m');
      box-shadow: 0 0 0 token('size/border/200') token('color/border/accent/focused/focused');
    }
  }
}
</style>
